import { useCallback, useState } from 'react';
import { DeleteDialog, ErpDataTableV2 } from '@/components/custom/erp';
import type { ImportBody, UUIDLookup } from './utils/excel-import';
import { handleImportExcel } from './utils/excel-import';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, ServiceDialog } from './components';
import { DichVu, DichVuInput } from '@/types/schemas';
import { useCRUD, useExcel, useForms } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';
import { buildDichVuPayload } from './utils';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 50;

export default function DichVuPage() {
  const { entity } = useAuth();
  const [isServiceDialogOpening, setIsServiceDialogOpening] = useState(false);
  const {
    data: dichVus,
    isLoading,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem: addDichVu,
    updateItem: updateDichVu,
    deleteItem: deleteDichVu,
    refreshData: refreshDichVus,
    searchData
  } = useCRUD<DichVu, DichVuInput>({
    endpoint: QUERY_KEYS.DICH_VU,
    pageSize: PAGE_SIZE
  });
  const { getUUIDByCode } = useExcel();

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<DichVu>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams, isFiltering } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : dichVus || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const MapServices = displayData.map(
    (service: DichVu): DichVu => ({
      ...service,
      dvt_name: service.dvt_data?.dvt || service.dvt,
      tk_dt_name: service.tk_dt_data?.code || service.tk_dt,
      tk_cp_name: service.tk_cp_data?.code || service.tk_cp,
      ma_thue_name: service.ma_thue_data?.ma_thue || service.ma_thue
    })
  );

  const entityModel = entity?.slug || '';

  const handleOpenServiceDialog = useCallback(async (openDialog: () => void | Promise<void>) => {
    setIsServiceDialogOpening(true);
    try {
      await openDialog();
    } finally {
      setIsServiceDialogOpening(false);
    }
  }, []);

  const handleAddService = useCallback(() => {
    handleAddClick();
  }, [handleAddClick]);

  const handleEditService = useCallback(() => {
    if (!selectedObj) return;
    void handleOpenServiceDialog(handleEditClick);
  }, [handleEditClick, handleOpenServiceDialog, selectedObj]);

  const handleViewService = useCallback(
    (row?: DichVu) => {
      if (!row && !selectedObj) return;
      void handleOpenServiceDialog(() => handleViewClick(row));
    },
    [handleOpenServiceDialog, handleViewClick, selectedObj]
  );

  const handleCopyService = useCallback(() => {
    if (!selectedObj) return;
    void handleOpenServiceDialog(handleCopyClick);
  }, [handleCopyClick, handleOpenServiceDialog, selectedObj]);

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addDichVu(buildDichVuPayload(body, 'NEW', entityModel));
      }
      await refreshDichVus();
      toast({
        description: 'Đã nhập danh mục dịch vụ từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Error importing services from Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi nhập danh mục dịch vụ từ Excel',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const handleImportExcelClick = () => {
    handleImportExcel(getUUIDByCode as unknown as UUIDLookup, handleImportBodies);
  };

  const tables = [
    {
      name: '',
      rows: MapServices,
      columns: getDataTableColumns(handleViewService)
    }
  ];

  const handleSubmit = async (data: DichVuInput) => {
    try {
      if (formMode === 'add') {
        await addDichVu({ ...data, action: 'NEW', entity_model: entityModel });
      } else if (formMode === 'edit' && selectedObj) {
        await updateDichVu(selectedObj.uuid, { ...data, action: 'EDIT', entity_model: entityModel });
      }
      refreshDichVus();
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <div className={'block'}>
        <ActionBar
          onAddIconClick={handleAddService}
          onEditIconClick={handleEditService}
          onDeleteIconClick={() => selectedObj && handleDeleteClick()}
          onCopyIconClick={handleCopyService}
          onWatchIconClick={() => handleViewService()}
          onRefreshClick={refreshDichVus}
          onImportExcel={handleImportExcelClick}
          isEditDisabled={!selectedObj}
          isViewDisabled={!selectedObj}
          isDeleteDisabled={!selectedObj}
          isCopyDisabled={!selectedObj}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          serverSidePagination={true}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
          loading={isLoading || isFiltering}
        />
      </div>

      {showForm && (
        <ServiceDialog
          open={showForm}
          mode={formMode}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={handleCopyClick}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteDichVu}
          clearSelection={clearSelection}
        />
      )}
    </div>
  );
}
